import type { ReactElement, ReactNode } from 'react';

export enum ExternalModuleName {
	React = 'react',
	ReactDOM = 'react-dom',
	ReactIntl = 'react-intl',
	ReactIntlNext = 'react-intl-next',
	EmotionReact = '@emotion/react',
	FeatureGateJsClientPublic = '@atlaskit/feature-gate-js-client',
	AnalyticsContext = '@atlaskit/analytics-next-stable-react-context',
	AnalyticsNext = '@atlaskit/analytics-next',
}

export type ExternalModule<T extends string = string> = {
	module: ExternalModuleName | T;
};

export type ExternalModuleManifest = {
	name: ExternalModuleName;
};

export type RemoteImportConfig = {
	moduleName: string;
};

export type RemoteModuleConfig<T extends string = string> = RemoteImportConfig & {
	externals: (ExternalModuleManifest | ExcludedSystemModule<T>)[];
	moduleUri: string;
};

export enum PluginName {
	AppSwitcher = 'app-switcher',
	PostOffice = 'post-office',
}

export type DefaultOpenProps = Record<string, any>;

export type RemoteComponentProps<P extends {} = DefaultOpenProps> = ComponentProps<P> & {
	/**
	 * The analytics channel to fire any events on
	 */
	analyticsChannel?: string;

	/**
	 * Children to be passed in to the remotely loaded component
	 */
	children?: ReactNode;

	/**
	 * Component to render when the ErrorBoundary is triggered
	 */
	ErrorFallback?: ReactElement | null;

	/**
	 * The export name to be used from the module. Will default to 'default'
	 */
	exportName?: 'default' | string;

	/**
	 * Component to render while waiting for component to load from remote module
	 */
	Fallback?: ReactElement | null;

	/**
	 * Callback to fire on different states of the remote component
	 */
	onEvent?: OnEvent;

	/**
	 * These are defined under /src/services/plugins
	 */
	remotePlugin: RemotePlugin;
};

export type ComponentProps<P extends {} = DefaultOpenProps> = P;

export type VariantLatest = 'latest';
export type VariantStable = 'stable';
export type PluginVariant = VariantLatest | VariantStable;
export type PluginEnv =
	| 'test-server'
	| 'test'
	| 'local'
	| 'dev'
	| 'staging'
	| 'production'
	| 'stg-fedm-east'
	| 'prod-fedm-east';
export type PluginDimensions = {
	env: PluginEnv;
	variant: PluginVariant;
};

export type ManifestMapValue = RemoteModuleConfig | Map<PluginVariant, RemoteModuleConfig>;

export type ManifestMap = Map<PluginEnv, ManifestMapValue>;

export type ModuleUriMapper = (
	baseUri: string,
	variant: PluginVariant | undefined,
	computeHash: (name: PluginVariant | string, dependencies: DependencyMap[]) => string,
) => string;

export type DependencyMap = {
	name: string;
	version: string;
};

export interface RemotePlugin {
	readonly bifrostOverrideKey?: string | undefined;
	readonly dimensions: PluginDimensions;
	readonly manifestMap: ManifestMap;

	readonly moduleUriMapper?: ModuleUriMapper | undefined;

	readonly name: PluginName;
}

export type ExternalModuleConfiguration = {
	import: any;
	isDefault: boolean;
};

export type ExcludedSystemModule<T extends string = string> = ExternalModuleConfiguration &
	ExternalModule<T>;

export type ExcludedSystemModuleForRegistry = ExternalModule & {
	import: any;
	scopedModuleId: string;
};

/** The importmap standard is defined here: https://github.com/WICG/import-maps */
export interface ImportMap {
	imports?: Record<string, string>;
	scopes?: Record<string, Record<string, string>>;
}

type Event = {
	error?: unknown;
	type: 'fetch-complete' | 'render-complete' | 'fetch-error' | 'render-error' | 'unmount';
};
export type OnEvent = (event: Event) => void;
